import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useQuery, gql } from '@apollo/client';
import { Header, Label, Message, Loader, Container, Icon, Checkbox } from 'semantic-ui-react';
import { ResponsiveContainer } from '../../../components/ResponsiveContainer';
import { useContentReviewerGate } from '../../../hooks/isContentReviewer';
import { PATHS } from '../../../paths';
import { parseApiDate } from '../../../utils/date';
import styles from './StreamTimeline.module.css';

/**
 * Scheduled posts, one row per stream.
 *
 * Left to right the row runs: furthest-future … next out │ just gone … furthest past, with the
 * divider where publication falls. One ordering rule across the whole row, so the divider is
 * the only thing you have to find.
 *
 * **Why chips and not a list.** Production has 1,192 scheduled posts, 638 of them in a single
 * stream. A list of cards is unreadable at that size and a megabyte over the wire; the server
 * sends the posts nearest to now on each side and the real totals, and the row says how many it
 * left out rather than silently truncating.
 */

const GET_TIMELINE = gql`
  query StreamTimeline($limit: Int) {
    streamTimeline(limit: $limit) {
      streamId
      streamName
      isDefault
      canManage
      scheduledTotal
      sentTotal
      scheduled { id title type publishAt isSent isRepost isTakenDown posterName }
      sent { id title type publishAt isSent isRepost isTakenDown posterName }
      feeds { id sourceStreamName delayMinutes }
    }
  }
`;

/** "straight away" / "45 min later" / "2 hours later" — the way somebody would say it. */
export const describeDelay = (minutes: number): string => {
    if (minutes <= 1) return 'straight away';
    if (minutes < 60) return `${minutes} min later`;
    if (minutes % 60 === 0) {
        const h = minutes / 60;
        return `${h} hour${h === 1 ? '' : 's'} later`;
    }
    const h = Math.floor(minutes / 60);
    return `${h}h ${minutes % 60}m later`;
};

const PostChip: React.FC<{ post: any; onOpen: (p: any) => void }> = ({ post, onOpen }) => {
    const when = parseApiDate(post.publishAt);

    const className = [
        styles.chip,
        post.isSent ? styles.chipSent : '',
        post.isRepost ? styles.chipRepost : '',
        post.isTakenDown ? styles.chipTakenDown : '',
    ].filter(Boolean).join(' ');

    return (
        <button
            type="button"
            className={className}
            onClick={() => onOpen(post)}
            title={`${post.title || '(no title)'}\n${when?.toLocaleString() ?? ''}${post.posterName ? `\n${post.posterName}` : ''}${post.isRepost ? '\nReposted by a replicator' : ''}${post.isTakenDown ? '\nTaken down' : ''}`}
        >
            <div className={styles.chipTitle}>{post.title || <em>(no title)</em>}</div>
            <div className={styles.chipWhen}>
                {when?.toLocaleDateString([], { day: 'numeric', month: 'short' })}
                {' '}
                {when?.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
            <div className={styles.chipMeta}>
                {post.isRepost && <Icon name="refresh" size="small" />}
                {post.isTakenDown ? 'Taken down' : post.posterName || post.type}
            </div>
        </button>
    );
};

const StreamRow: React.FC<{ row: any; onOpen: (p: any) => void }> = ({ row, onOpen }) => {
    const trackRef = useRef<HTMLDivElement>(null);
    const dividerRef = useRef<HTMLDivElement>(null);

    /**
     * Open with the divider in view.
     *
     * A row of 638 chips starts scrolled to the far left — which is the furthest-future post,
     * the least interesting thing on the row. What somebody opens this screen for is what is
     * about to go out and what just went, and that is exactly where the divider sits.
     */
    useEffect(() => {
        const track = trackRef.current;
        const divider = dividerRef.current;
        if (track == null || divider == null) return;
        track.scrollLeft = Math.max(0, divider.offsetLeft - track.clientWidth / 2);
    }, [row.streamId, row.scheduled.length, row.sent.length]);

    const nothing = row.scheduledTotal === 0 && row.sentTotal === 0;
    const hiddenFuture = Math.max(0, row.scheduledTotal - row.scheduled.length);
    const hiddenPast = Math.max(0, row.sentTotal - row.sent.length);

    return (
        <div className={styles.streamRow}>
            <div className={styles.streamHead}>
                <span className={styles.streamName}>{row.streamName}</span>
                {row.isDefault && <Label size="mini" basic>default</Label>}
                <span className={styles.counts}>
                    {row.scheduledTotal} scheduled · {row.sentTotal} sent
                </span>

                {row.feeds.length > 0 && (
                    <span className={styles.feeds}>
                        {row.feeds.map((f: any) => (
                            <Label
                                key={f.id}
                                size="mini"
                                color="teal"
                                basic
                                title={`A replicator reposts everything published in ${f.sourceStreamName} into this stream, ${describeDelay(f.delayMinutes)}. Those posts are not listed here until they are made.`}
                            >
                                <Icon name="refresh" />
                                from {f.sourceStreamName}, {describeDelay(f.delayMinutes)}
                            </Label>
                        ))}
                    </span>
                )}
            </div>

            {nothing ? (
                <div className={styles.emptyTrack}>Nothing scheduled or sent.</div>
            ) : (
                <div className={styles.track} ref={trackRef}>
                    {hiddenFuture > 0 && (
                        <span className={styles.moreChip}>+{hiddenFuture} further out</span>
                    )}

                    {row.scheduled.map((p: any) => (
                        <PostChip key={p.id} post={p} onOpen={onOpen} />
                    ))}

                    {/* Where publication falls. Present even when one side is empty, so the
                        row never leaves you guessing which side you are looking at. */}
                    <div className={styles.divider} ref={dividerRef}>
                        <span className={styles.dividerLabel}>NOW</span>
                    </div>

                    {row.sent.map((p: any) => (
                        <PostChip key={p.id} post={p} onOpen={onOpen} />
                    ))}

                    {hiddenPast > 0 && (
                        <span className={styles.moreChip}>+{hiddenPast} older</span>
                    )}
                </div>
            )}
        </div>
    );
};

const PostsScheduledView: React.FC = () => {
    // Redirect gate, not just a hidden nav item — defence in depth rather than relying on the
    // backend's 403 alone.
    const { loading: meLoading, isContentReviewer } = useContentReviewerGate();
    const navigate = useNavigate();
    const [showEmpty, setShowEmpty] = useState(false);

    const { data, loading, error } = useQuery(GET_TIMELINE, {
        variables: { limit: 25 },
        fetchPolicy: 'network-only',
    });

    if (!meLoading && !isContentReviewer) {
        return <Navigate to={PATHS.CLASSES} replace />;
    }

    if (loading) {
        return (
            <ResponsiveContainer>
                <Loader active inline="centered" style={{ marginTop: '5em' }}>Loading streams...</Loader>
            </ResponsiveContainer>
        );
    }
    if (error) {
        return (
            <ResponsiveContainer>
                <Container text><Message error content="Could not fetch the stream timeline." /></Container>
            </ResponsiveContainer>
        );
    }

    const rows = data?.streamTimeline || [];

    // A stream with a replicator pointed at it is not idle — it is waiting. Keep it visible
    // even with nothing in it yet, or the row explaining where posts will come from is the one
    // thing that gets collapsed.
    const isEmpty = (r: any) => r.scheduledTotal === 0 && r.sentTotal === 0 && r.feeds.length === 0;
    const active = rows.filter((r: any) => !isEmpty(r));
    const empty = rows.filter(isEmpty);

    const open = (post: any) => {
        // Sent posts have their own editor with the take-down controls; scheduled ones the
        // scheduling editor.
        navigate(post.isSent ? `/posts/sent/${post.id}` : `/posts/scheduled/${post.id}`);
    };

    const totalScheduled = rows.reduce((n: number, r: any) => n + r.scheduledTotal, 0);

    return (
        <ResponsiveContainer>
            <Container fluid className={styles.pageContainer}>
                <div className={styles.headerSection}>
                    <Header as="h1" style={{ margin: 0 }}>Scheduled Posts</Header>
                    <Label color="blue" size="tiny">{totalScheduled} scheduled</Label>
                    {empty.length > 0 && (
                        <Checkbox
                            toggle
                            label={`Show ${empty.length} empty stream${empty.length === 1 ? '' : 's'}`}
                            checked={showEmpty}
                            onChange={() => setShowEmpty((v) => !v)}
                            style={{ marginLeft: 'auto' }}
                        />
                    )}
                </div>

                {rows.length === 0 && <Message info content="You cannot see any streams yet." />}

                {active.map((row: any) => (
                    <StreamRow key={row.streamId} row={row} onOpen={open} />
                ))}

                {showEmpty && empty.map((row: any) => (
                    <StreamRow key={row.streamId} row={row} onOpen={open} />
                ))}

                {!showEmpty && empty.length > 0 && (
                    <div className={styles.collapsedNote}>
                        {empty.length} stream{empty.length === 1 ? '' : 's'} with nothing scheduled or sent
                        {' '}&mdash; collapsed.
                    </div>
                )}
            </Container>
        </ResponsiveContainer>
    );
};

export { PostsScheduledView };
